import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { isValidInMobile } from "../../lib/mobile";

/**
 * Reusable form dialog for create / edit.
 * fields: [{ name, label, type: 'text'|'tel'|'date'|'select'|'textarea', required?, placeholder?, options?: [{value,label}], halfWidth?: bool }]
 */
export const EntityDialog = ({
  open,
  onOpenChange,
  title,
  description,
  fields,
  initialValues = {},
  onSubmit,
  submitLabel = "Save",
  testIdPrefix = "entity-dialog",
  validate: extraValidate,
}) => {
  const [values, setValues] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setValues(initialValues || {});
      setErrors({});
    }
  }, [open, initialValues]);

  const update = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const validate = () => {
    const next = {};
    fields.filter((f) => !f.showIf || f.showIf(values)).forEach((f) => {
      const v = values[f.name];
      if (f.required && (v === undefined || v === null || String(v).trim() === "")) {
        next[f.name] = `${f.label} is required`;
      }
      if (f.type === "mobile" && v && !isValidInMobile(v)) {
        next[f.name] = "Enter a valid 10-digit Indian mobile number (starts with 6–9)";
      }
      if (f.type === "tel" && v && !/^[+\d][\d\s-]{6,19}$/.test(String(v))) {
        next[f.name] = "Enter a valid phone number";
      }
    });
    Object.assign(next, extraValidate ? extraValidate(values) : {});
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setSubmitting(true);
      await onSubmit(values);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white" data-testid={`${testIdPrefix}-content`}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-emerald-950">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields.filter((f) => !f.showIf || f.showIf(values)).map((f) => (
            <div key={f.name} className={f.halfWidth ? "sm:col-span-1" : "sm:col-span-2"}>
              <Label className="text-xs font-semibold uppercase tracking-wider text-emerald-900/70">
                {f.label}{f.required ? " *" : ""}
              </Label>
              {f.type === "select" ? (
                <Select
                  value={values[f.name] ?? ""}
                  onValueChange={(v) => update(f.name, v)}
                >
                  <SelectTrigger className="mt-1.5 h-10 bg-white" data-testid={`${testIdPrefix}-${f.name}`}>
                    <SelectValue placeholder={f.placeholder || "Select…"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {(f.options || []).map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={f.type === "mobile" ? "tel" : f.type || "text"}
                  value={values[f.name] ?? ""}
                  onChange={(e) => update(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`${testIdPrefix}-${f.name}`}
                  className="mt-1.5 h-10"
                />
              )}
              {errors[f.name] && (
                <p className="mt-1 text-xs text-rose-700">{errors[f.name]}</p>
              )}
            </div>
          ))}

          <DialogFooter className="sm:col-span-2 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid={`${testIdPrefix}-cancel`}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#0F4C3A] text-white hover:bg-emerald-900"
              data-testid={`${testIdPrefix}-submit`}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
